import { UseFormReturn } from "react-hook-form";
import { ZodSchema } from "zod";
import { useLoginMutation } from "./useLoginMutation";
import { useGoogleLoginMutation } from "./useGoogleLoginMutation";
import { useTrackLoginEvents } from "./useTrackLoginEvents";
import { baseLoginSchema } from "../auth.schemas";

export type LoginFormData = {
  email: string;
  password: string;
};

export type UseLoginProps = {
  /** Success callback */
  onSuccess?: (data: any) => void;
  /** Error callback */
  onError?: () => void;
  /** Custom schema (defaults to baseLoginSchema) */
  schema?: ZodSchema;
};

export type UseLoginReturn = {
  /** Login mutation for form submission */
  loginMutation: ReturnType<typeof useLoginMutation>;
  /** Google login mutation */
  googleLoginMutation: ReturnType<typeof useGoogleLoginMutation> | null;
  /** Schema for form validation */
  schema: ZodSchema;
  /** Default values for form */
  defaultValues: LoginFormData;
};

/**
 * Composite hook for login functionality.
 * Combines login mutation, Google login, and analytics tracking.
 *
 * Usage:
 * ```tsx
 * const { loginMutation, googleLoginMutation, schema, defaultValues } = useLogin({
 *   onSuccess: (data) => navigate('/dashboard'),
 *   onError: () => toast.error('Login failed'),
 * });
 *
 * const { form, handleSubmit } = useForm({
 *   schema,
 *   onSubmit: loginMutation.mutateAsync,
 *   defaultValues,
 * });
 * ```
 */
export function useLogin({
  onSuccess,
  onError,
  schema = baseLoginSchema,
}: UseLoginProps = {}): UseLoginReturn {
  // Track login intent and started events
  useTrackLoginEvents();

  // Email/password login mutation
  const loginMutation = useLoginMutation({
    onSuccess,
    onError,
    schema,
  });

  // Google login is initialized lazily when form is available
  // Return null here - styled component will set up with form
  const googleLoginMutation = null;

  const defaultValues: LoginFormData = {
    email: "",
    password: "",
  };

  return {
    loginMutation,
    googleLoginMutation,
    schema,
    defaultValues,
  };
}

/**
 * Hook for Google login - requires form instance.
 * Call this after useForm to get Google login functionality.
 */
export function useGoogleLogin(props: {
  form: UseFormReturn<any>;
  onSuccess?: (data: any) => void;
  onError?: () => void;
}) {
  return useGoogleLoginMutation({
    form: props.form,
    onSuccess: props.onSuccess,
    onError: props.onError,
  });
}

export default useLogin;

